// İSG Portal — Modal (Radix Dialog sarmalayıcısı). Erişilebilir: odak tuzağı,
// Escape ile kapanış, `aria-labelledby`/`aria-describedby`. Katman token'ı
// `--z-modal` ([[feedback_ui_layer_order]]); literal renk/z-index yok.
//
// **Kapanışta tetikleyiciye odak dönüşü (S33 Slice 7 QA fix, HIGH — `S33-7-final-qa-A.md` +
// `-B.md`, aynı kök neden `Sheet.tsx`'in dosya başlığındaki not).** Bu bileşen de KASITLI
// OLARAK bir `Dialog.Trigger` render ETMEZ (her tüketici kendi açan düğmesini kendi JSX'inde
// tutar — TEK bir sabit Trigger tüm çağrı yerlerini SARAMAZ). Radix bunu bilmediği için
// kapanışta odağı hiçbir yere DÖNDÜRMÜYORDU (canlı QA, Esc ile — bir sonraki Tab dokümandaki
// İLK odaklanabilir öğeye, ör. dil değiştiriciye düşüyordu). `Sheet.tsx`'in ÇÖZÜMÜNDEN
// (çağıranın kendi `restoreFocusRef`'ini GEÇMESİ) FARKLI OLARAK burada SİSTEMİK bir çözüm
// seçildi — **hiçbir Modal/ConfirmDialog tüketicisi değişmez**: `open` `false→true` geçişinde
// (render SIRASINDA, herhangi bir `useEffect`TEN ÖNCE — Radix'in kendi `onOpenAutoFocus`'u
// odağı diyaloğun İÇİNE taşımadan ÖNCE) `document.activeElement` (tıklanan düğme) bir ref'e
// YAKALANIR; `onCloseAutoFocus`'ta Radix'in (burada işe yaramayan) varsayılanının YERİNE
// oraya `.focus()` çağrılır. Render sırasında ref MUTATE ETMEK bilinen bir React deseni
// (`useRef`ile "önceki prop" izleme) — render ÇIKTISINI etkilemez, yalnız bir sonraki
// commit için bir değer HAZIRLAR.
//
// **İKİNCİ tüketici deseni: `open` başta zaten `true` iken MOUNT (S33 Slice 7 QA-recheck fix).**
// `PermitLifecycleActions.tsx`/`RiskLifecycleActions.tsx` gibi bazı çağıranlar `<ConfirmDialog>`u
// KOŞULLU render eder (`{activeConfirm ? <ConfirmDialog open .../> : null}`) — bileşen `open=
// false`ken hiç YOK, doğrudan `open=true` ile DOĞAR. Yukarıdaki `false→true` GEÇİŞİ bunu asla
// YAKALAMAZ (ref zaten `true` ile başlar, "geçiş" hiç olmaz) — `restoreFocusRef` boş kalır. Bu
// yüzden ref'in İLK DEĞERİ de aynı `document.activeElement`'i (mount ANINDA `open` zaten
// `true`yse) yakalar. AYRICA aynı tüketici deseni kapanışta `<ConfirmDialog>`u TAMAMEN SÖKER
// (`activeConfirm` de `null` olur) — bu, Radix'in `Dialog.Content`'ini `onCloseAutoFocus`
// ÇALIŞTIRMADAN, tüm alt ağaçla birlikte aynı anda unmount eder (Presence'ın geçiş süresi
// TANIMADIĞI bir durum, çünkü SARAN ternary de aynı anda `null`a düşer). Bu ikinci yolu
// kapatmak için `open` iken UNMOUNT olursa da (React'in kendi unmount temizlemesinde, DEĞİL
// bir `onCloseAutoFocus` olayında) aynı hedefe `.focus()` çağrılır — hedefin hâlâ belgede
// olduğu KORUNARAK (sayfa geneli bir gezinmede tetikleyici de zaten sökülmüş olabilir).
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from './cn';

/** Gövdede odaklanabilir SAYILAN alan türleri (S36 #8) — devre dışı olanlar hariç. */
export const FOCUSABLE_FIELD_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]';

/** Modal VE Sheet footer'ının TEK sınıf kaynağı (Carbon: eşit hücreli, boşluksuz grid). */
export function modalFooterClass(footerColumns: 1 | 2 | 3): string {
  return cn(
    'flex shrink-0 items-center justify-end gap-2 border-t border-border p-5 pt-4',
    'carbon:grid carbon:min-h-16 carbon:items-stretch carbon:gap-0 carbon:border-0 carbon:p-0 carbon:[&>*]:h-full',
    // Carbon: etiket hücrenin SOL ÜSTÜNDE (S33 Slice 7 "footer buttons are text-left").
    'carbon:[&>*]:items-start carbon:[&>*]:justify-start carbon:[&>*]:px-4 carbon:[&>*]:pb-4 carbon:[&>*]:pt-3.5 carbon:[&>*]:text-left',
    footerColumns === 1 && 'carbon:grid-cols-1',
    footerColumns === 3 && 'carbon:grid-cols-3',
    footerColumns === 2 && 'carbon:grid-cols-2',
  );
}

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Alt aksiyon çubuğu (butonlar) — verilirse içerikten ayrılır. */
  footer?: ReactNode;
  /**
   * Açılışta özel bir odak hedefi (S36 #8, nadiren gerekir). Verilmezse varsayılan davranış:
   * gövdedeki İLK etkin input/textarea/select, o da yoksa footer'daki İLK buton (ConfirmDialog'da
   * bu HER ZAMAN "Vazgeç"tir — onay/tehlikeli aksiyon dosya başlığı notunda da geçtiği gibi HER
   * ZAMAN sonda döner, `variant={confirmVariant}`; "son butonu odakla" tehlikeli aksiyona
   * odaklanırdı, bu yüzden BİLİNÇLİ olarak İLK buton seçilir — WAI-ARIA APG alert-dialog'un
   * "en az yıkıcı aksiyon" varsayılanı). Radix'in kendi varsayılanı (Dialog.Content'teki İLK
   * odaklanabilir öğe — pratikte ✕ kapat düğmesi, header'da her zaman ilk sırada) hiçbir zaman
   * seçilmez; Enter'ın diyaloğu SESSİZCE kapatması bu maddenin bulduğu kusurdu.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Kapanışta odağın döneceği öğe. Menü öğesinden (`RowActionsMenu`) açılan modallarda yakalanan
   * `document.activeElement` menü öğesidir ve menü kapanınca DOM'dan kalkar → odak `<body>`'ye
   * düşer. Verilirse ve `current`ı DOM'da bağlıysa o tercih edilir; aksi hâlde (verilmedi ya da
   * bağlantısı kopuk) mevcut yakalanan-öğe davranışı AYNEN sürer.
   */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Carbon footer'ının eşit-genişlikli sütun sayısı (S33-8 review fix) — DOM'dan OTOMATİK
   * SAYILMAZ: `footer` çoğu çağıranda TEK bir `<>…</>` fragment'i olarak gelir, ve
   * `React.Children.count` bir Fragment'i AÇMAZ (TableToolbar'ın Slice 4 dosya başlığı
   * notundaki AYNI ders — 3. tur, "Children.map çözümü YANLIŞTI"). Varsayılan `2` bugünkü TÜM
   * tüketicilerin (27 ConfirmDialog + ~29 form-modal) şekli; 3 buton isteyen tek çağıran
   * (`PermitFormModal` create modu) açıkça geçer.
   */
  footerColumns?: 1 | 2 | 3;
  /** Kapat düğmesinin erişilebilir etiketi (i18n ile). */
  closeLabel?: string;
  /** İçerik genişliği (varsayılan orta). */
  size?: 'sm' | 'md' | 'lg' | 'full';
}

const sizes: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  full: 'max-w-[95vw]',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  footerColumns = 2,
  closeLabel = 'Kapat',
  size = 'md',
  initialFocusRef,
  restoreFocusRef: restoreTargetRef,
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  // Dosya başlığı notu — `open` `false→true` geçişinde (render sırasında, Radix'in kendi
  // odak-taşıma efektinden ÖNCE) tetikleyiciyi yakalar. İlk değer `open` zaten `true`yken
  // MOUNT olan tüketiciler içindir (dosya başlığının "ikinci tüketici deseni" notu) — o
  // durumda "geçiş" hiç olmaz, ref'in kendi ilk değeri yakalamak ZORUNDA.
  const wasOpenRef = useRef(open);
  const restoreFocusRef = useRef<HTMLElement | null>(
    open ? (document.activeElement as HTMLElement | null) : null,
  );
  if (open && !wasOpenRef.current) {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
  }
  wasOpenRef.current = open;
  // `open`ken UNMOUNT olan tüketiciler için (dosya başlığı notu) — bu, `onCloseAutoFocus`un
  // hiç ÇALIŞMADIĞI ikinci kapanış yolu; boş bağımlılık dizisi = temizleme yalnız GERÇEK
  // unmount'ta çalışır, `openRef`/`restoreFocusRef` HER render'da güncellendiğinden temizleme
  // her zaman GÜNCEL değerleri okur (eski kapanışa TAKILMAZ).
  const openRef = useRef(open);
  openRef.current = open;
  const restoreTargetHolder = useRef(restoreTargetRef);
  restoreTargetHolder.current = restoreTargetRef;
  useEffect(() => {
    return () => {
      if (!openRef.current) {
        return;
      }
      // Çağıranın açık `restoreFocusRef`'i (`onCloseAutoFocus` ile aynı öncelik) — `key` ile
      // yeniden mount edilen tüketicide yakalanan tetikleyici DOM'dan kalkmış olabilir.
      const preferred = restoreTargetHolder.current?.current;
      if (preferred?.isConnected) {
        preferred.focus();
        return;
      }
      const target = restoreFocusRef.current;
      if (target && target !== document.body && document.contains(target)) {
        target.focus();
      }
    };
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* `bg-fg/40` → carbon `bg-fg/50` (README `rgba(22,22,22,.5)`, plan §2.7 token
            çevirisi — `--color-fg` her iki derlemede de neredeyse aynı koyu değere düşer). */}
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-modal)] bg-fg/40 backdrop-blur-[1px] carbon:bg-fg/50 carbon:backdrop-blur-none" />
        <Dialog.Content
          ref={contentRef}
          onOpenAutoFocus={(event) => {
            // S36 #8 — Radix'in varsayılanı (İLK odaklanabilir öğe, DOM sırasında ✕ kapat
            // düğmesi) hiçbir zaman kabul edilmez; her durumda ÖNCE `preventDefault` edilip
            // aşağıdaki sıradaki hedefe elle odaklanılır.
            if (initialFocusRef?.current) {
              event.preventDefault();
              initialFocusRef.current.focus();
              return;
            }
            const content = contentRef.current;
            if (!content) {
              return;
            }
            const body = content.querySelector<HTMLElement>('[data-tile]');
            const firstField = body?.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR);
            if (firstField) {
              event.preventDefault();
              firstField.focus();
              return;
            }
            const footerEl = content.querySelector<HTMLElement>('[data-modal-footer]');
            const firstButton =
              footerEl?.querySelector<HTMLButtonElement>('button:not([disabled])');
            if (firstButton) {
              event.preventDefault();
              firstButton.focus();
            }
            // Gövde de footer da odaklanabilir bir şey içermiyorsa (nadir — salt-okunur bir
            // özet diyaloğu) Radix'in kendi varsayılanına düşülür (`preventDefault`
            // ÇAĞRILMAZ) — bu durumda ✕ kabul edilebilir bir son çaredir.
          }}
          onCloseAutoFocus={(event) => {
            const preferred = restoreTargetRef?.current;
            if (preferred?.isConnected) {
              event.preventDefault();
              preferred.focus();
              return;
            }
            const target = restoreFocusRef.current;
            if (target && target !== document.body && document.contains(target)) {
              event.preventDefault();
              target.focus();
            }
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-[var(--z-modal)] flex w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'overflow-hidden rounded-lg border border-border bg-surface shadow-lg',
            // `max-h` + `flex-col` + gövdenin KENDİ `overflow-y-auto`'su (aşağıda) — S33-8 review
            // fix: Carbon "Dialog"da yalnız gövde kayar, başlık/footer SABİT kalır (bugüne kadar
            // TÜM `Dialog.Content` kayıyordu, footer içerikle birlikte aşağı/yukarı itiliyordu).
            // Dış kutunun `overflow-hidden`ı YENİ (gövde artık AYRI kayıyor) — `rounded-lg`/
            // `carbon:rounded-none`in köşelerini gerçekten KIRPMASI için (border-radius tek
            // başına çocukları kırpmaz, bir `overflow` değeri GEREKİR); gövdenin KENDİ
            // `overflow-y-auto`su hâlâ dikey kaydırmayı yapan o.
            'max-h-[calc(100vh-2rem)] focus:outline-none',
            // Carbon Stage 2 "Dialog → Modal": kenarlıksız/gölgesiz/köşesiz, 640px (dar viewport'ta `100vw-2rem`'e kadar daralır, kırpılmaz)
            // (size prop'unun max-w'sini EZER — README tek genişlik verir, `sm/lg/full`
            // boyutları bu dilimde hâlâ web-expert dışı hiçbir tüketiciyi etkilemez).
            // `carbon:max-w-none` gerekli: `max-w-*` (sizes[size]) `width` DEĞİL, AYRI bir
            // CSS özelliği — cascade sırası onu ezmez, tarayıcı ikisini de UYGULAR ve `sm`/`md`
            // boyutlarında 640px'i kendi (daha dar) `max-w-lg`'ine KIRPARDI. `min()` genişliği viewport'a sınırlar; `max-w-none` gerekçesi aynen geçerli.
            'carbon:rounded-none carbon:border-0 carbon:shadow-none carbon:w-[min(640px,calc(100vw-2rem))] carbon:max-w-none',
            sizes[size],
          )}
        >
          <div
            className={cn(
              'flex shrink-0 items-start justify-between gap-4 border-b border-border p-5 pb-4',
              'carbon:border-0 carbon:p-4 carbon:pr-12',
            )}
          >
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-lg font-semibold text-fg carbon:text-xl carbon:font-normal carbon:leading-7">
                {title}
              </Dialog.Title>
              {/* Açıklama YOKSA hiçbir şey basılmaz. Burada bir zamanlar BAŞLIĞI tekrarlayan
                  `sr-only` bir Description dururdu — yalnız Radix'in "Description eksik"
                  uyarısını susturmak için — ve ekran okuyucu her diyalogda aynı cümleyi İKİ
                  KEZ okuyordu (S19 QA D6). O uyarı `@radix-ui/react-dialog` 1.1.x'te ARTIK
                  YOK: `Dialog.Content` `aria-describedby`'ı zaten `descriptionPresent`e göre
                  koşullu basıyor, yani açıklamasız diyalog artık askıda bir IDREF de
                  bırakmıyor. Açıklama VEREN çağrılar aynen çalışır. */}
              {description ? (
                <Dialog.Description className="text-sm text-muted">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              className={cn(
                'rounded-md p-1 text-muted hover:bg-surface-muted hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'carbon:absolute carbon:right-0 carbon:top-0 carbon:h-12 carbon:w-12 carbon:rounded-none carbon:p-0',
              )}
              aria-label={closeLabel}
            >
              <X className="mx-auto h-5 w-5" aria-hidden="true" />
            </Dialog.Close>
          </div>

          {/* `data-tile` — Card.tsx'in AYNI işareti (S33 Slice 6, global düzeltme #2): Modal
              gövdesi de beyaz bir Carbon yüzeyi, İÇİNDEKİ form alanları `Card.tsx`'teki gerekçeyle
              AYNI gri dolguyu ister. `min-h-0` + `overflow-y-auto` (S33-8 review fix) — bu artık
              Dialog.Content'in İÇİNDEKİ TEK kayan eleman (`flex-col` ebeveynde `min-height:auto`
              varsayılanı büzülmeyi ENGELLERdi, `min-h-0` onu açar). */}
          <div
            data-tile
            className="min-h-0 flex-1 overflow-y-auto p-5 carbon:px-4 carbon:pb-12 carbon:pt-0 carbon:text-muted"
          >
            {children}
          </div>

          {footer ? (
            // `data-modal-footer` — SADECE bu footer'ın kendi grid düzeni İÇİN bir işaret
            // (owner review B1 item 4 ile `Button.tsx`'in artık OKUMADIĞI eski bir DOM-kapsam
            // mekanizmasının kalıntısı — `secondary` artık HER YERDE tek dolu-gri biçim, bkz. o
            // dosyanın başlığı). Carbon footer'ı eşit hücreli, boşluksuz bir grid'e döner (README
            // "equal buttons, h-16, text-left, no gap") — hücre SAYISI `footerColumns`tan gelir
            // (yukarıdaki prop notu).
            <div data-modal-footer className={modalFooterClass(footerColumns)}>
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
